import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close"; // 閉じるボタン用のアイコン
import FastfoodIcon from "@mui/icons-material/Fastfood"; //食事アイコン
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { transactionSchema } from "../validations/schema";
import { WorkoutContext } from "./provider/WorkoutProvider";
import AddCircleIcon from "@mui/icons-material/AddCircle";

const TransactionForm = ({ onCloseForm, isEntryDrawerOpen, currentDay }) => {
  const {
    transactions,
    setTransactions,
    selectedTransaction,
    onSaveTransaction,
    onDeleteTransaction,
    onUpdateTransaction,
    setSelectedTransaction,
  } = useContext(WorkoutContext);
  const formWidth = 320;

  const upperBodyWorkouts = [
    { label: "サイドレイズ", icon: <FastfoodIcon fontSize="small" /> },
    { label: "アームカール", icon: <FastfoodIcon fontSize="small" /> },
    { label: "ベンチプレス", icon: <FastfoodIcon fontSize="small" /> },
    { label: "チンニング", icon: <FastfoodIcon fontSize="small" /> },
    { label: "フロントプレス", icon: <FastfoodIcon fontSize="small" /> },
    { label: "ベントオーバーロウ", icon: <FastfoodIcon fontSize="small" /> },
  ];

  const lowerBodyWorkouts = [
    { label: "スクワット", icon: <FastfoodIcon fontSize="small" /> },
    { label: "デッドリフト", icon: <FastfoodIcon fontSize="small" /> },
  ];

  const cardioWorkouts = [
    { label: "ランニング", icon: <FastfoodIcon fontSize="small" /> },
    { label: "スイミング", icon: <FastfoodIcon fontSize="small" /> },
  ];

  const [workouts, setWorkouts] = useState(upperBodyWorkouts);

  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
    handleSubmit,
    reset,
    register,
  } = useForm({
    defaultValues: {
      category: "upperBody",
      date: currentDay,
      workout: "サイドレイズ",
      detail: "",
      workoutSet: [{ set_no: 1, weight: 0, rep: 1 }],
    },
    resolver: zodResolver(transactionSchema),
  });

  const { fields, remove, append, replace } = useFieldArray({
    control,
    name: "workoutSet",
    rules: {
      minLength: 1,
    },
  });

  // wrokoutカテゴリー(上半身、下半身、有酸素の切り替え)
  const workoutCategoryToggle = (category) => {
    setValue("category", category); // react hook formの項目と値を指定
    setValue("workout", "");
  };

  const currentCategory = watch("category"); // react hook formのcategoryの値を監視する

  useEffect(() => {
    const newWorkouts =
      currentCategory === "upperBody"
        ? upperBodyWorkouts
        : currentCategory === "lowerBody"
          ? lowerBodyWorkouts
          : cardioWorkouts;
    setWorkouts(newWorkouts);
  }, [currentCategory]);

  useEffect(() => {
    setValue("date", currentDay);
  }, [currentDay]);

  // セットを削除する処理
  const handleRemoveSet = (index) => {
    // remove(index);
    // インデックスを更新する処理
    const updateSets = getValues("workoutSet")
      .filter((_, i) => i !== index)
      .map((set, index) => ({
        ...set,
        set_no: index + 1,
      }));
    replace(updateSets);
  };

  // セットを追加する処理
  const handleAddSet = () => {
    const currentSets = getValues("workoutSet");
    append({ set_no: currentSets.length + 1, weight: 0, rep: 1 });
  };

  // 送信処理
  const onSubmit = (data) => {
    // 更新処理
    if (selectedTransaction) {
      onUpdateTransaction(data, selectedTransaction.id);
      setSelectedTransaction(null);
    } else {
      //　新規登録処理
      onSaveTransaction(data);
    }
    reset({
      date: currentDay,
      category: "upperBody",
      date: currentDay,
      workout: "サイドレイズ",
      detail: "",
      workoutSet: [{ set_no: 1, weight: 0, rep: 1 }],
    });
    // dateを含めないと毎回今日の日付で初期化される
  };

  useEffect(() => {
    // ワークアウトの選択肢が更新されたか確認
    if (selectedTransaction) {
      const categoryExists = workouts.some(
        (workout) => workout.label === selectedTransaction.exercise,
      );
      setValue("workout", categoryExists ? selectedTransaction.exercise : "");
    }
  }, [selectedTransaction, workouts]);

  // フォームの内容を選択したワークアウトに合わせて更新
  useEffect(() => {
    if (selectedTransaction) {
      setValue("category", selectedTransaction.category);
      setValue("date", selectedTransaction.date);
      setValue("detail", selectedTransaction.memo);
      setValue(
        "workoutSet",
        selectedTransaction.sets.map((set) => ({
          ...set,
          set_no: parseInt(set.set_no),
          weight: parseInt(set.weight),
          rep: parseInt(set.rep),
        })),
      );
    } else {
      reset({
        date: currentDay,
        category: "upperBody",
        date: currentDay,
        workout: "サイドレイズ",
        detail: "",
        workoutSet: [{ set_no: 1, weight: 0, rep: 1 }],
      });
    }
  }, [selectedTransaction]);

  const handleDelete = () => {
    if (selectedTransaction) {
      onDeleteTransaction(selectedTransaction.id);
      setSelectedTransaction(null);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 64,
        right: isEntryDrawerOpen ? formWidth : "-2%", // フォームの位置を調整
        width: formWidth,
        height: "100%",
        bgcolor: "background.paper",
        zIndex: (theme) => theme.zIndex.drawer - 1,
        transition: (theme) =>
          theme.transitions.create("right", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        p: 2, // 内部の余白
        boxSizing: "border-box", // ボーダーとパディングをwidthに含める
        boxShadow: "0px 0px 15px -5px #777777",
      }}
    >
      {/* 入力エリアヘッダー */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
        mb={2}
      >
        <Typography variant="h6">入力</Typography>
        {/* 閉じるボタン */}
        <IconButton
          onClick={onCloseForm}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {/* フォーム要素 */}
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          {/* カテゴリ切り替えボタン */}
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <ButtonGroup fullWidth>
                <Button
                  variant={
                    field.value === "upperBody" ? "contained" : "outlined"
                  }
                  color="primary"
                  //   sx={{
                  //     bgcolor: (theme) => theme.palette.upperBody.dark,
                  //   }}
                  onClick={() => workoutCategoryToggle("upperBody")}
                >
                  上半身
                </Button>
                <Button
                  variant={
                    field.value === "lowerBody" ? "contained" : "outlined"
                  }
                  color="error"
                  onClick={() => workoutCategoryToggle("lowerBody")}
                >
                  下半身
                </Button>
                <Button
                  variant={field.value === "cardio" ? "contained" : "outlined"}
                  color="secondary"
                  onClick={() => workoutCategoryToggle("cardio")}
                >
                  有酸素運動
                </Button>
              </ButtonGroup>
            )}
          />

          {/* 日付 */}
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="日付"
                type="date"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                helperText={errors.date?.message}
              />
            )}
          />

          {/* ワークアウト */}
          <Controller
            name="workout"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="ワークアウト"
                label="ワークアウト"
                select
                error={!!errors.workout}
                helperText={errors.workout?.message}
              >
                {workouts.map((workout, index) => (
                  <MenuItem value={workout.label} key={index}>
                    <ListItemIcon>{workout.icon}</ListItemIcon>
                    {workout.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {/* 内容 */}
          <Controller
            name="detail"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="内容"
                type="text"
                error={!!errors.detail}
                helperText={errors.detail?.message}
              />
            )}
          />
          {/* セット */}
          <Box sx={{ maxHeight: "35vh", overflowY: "auto" }}>
            <List
              subheader={
                <ListSubheader>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 3 }} display={"flex"}>
                      <Typography noWrap sx={{ fontSize: ".8rem" }}>
                        セット
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }} display={"flex"}>
                      <Typography noWrap sx={{ fontSize: ".8rem" }}>
                        重量(kg)
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }} display={"flex"}>
                      <Typography noWrap sx={{ fontSize: ".8rem" }}>
                        レップ
                      </Typography>
                    </Grid>
                  </Grid>
                </ListSubheader>
              }
            >
              {fields.map((item, index) => {
                return (
                  <ListItem key={item.id} sx={{ px: 0 }}>
                    <Controller
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          error={!!errors.workoutSet?.[index]?.set_no}
                          helperText={
                            errors.workoutSet?.[index]?.set_no?.message
                          }
                          value={field.value === 0 ? "" : field.value}
                          onChange={(e) => {
                            const newvalue = parseInt(e.target.value) || 0;
                            field.onChange(newvalue);
                          }}
                        />
                      )}
                      name={`workoutSet.${index}.set_no`}
                      control={control}
                    />
                    <Controller
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          error={!!errors.workoutSet?.[index]?.weight}
                          helperText={
                            errors.workoutSet?.[index]?.weight?.message
                          }
                          value={field.value === 0 ? "" : field.value}
                          onChange={(e) => {
                            const newvalue = parseInt(e.target.value) || 0;
                            field.onChange(newvalue);
                          }}
                        />
                      )}
                      name={`workoutSet.${index}.weight`}
                      control={control}
                    />
                    <Controller
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          error={!!errors.workoutSet?.[index]?.rep}
                          helperText={errors.workoutSet?.[index]?.rep?.message}
                          value={field.value === 0 ? "" : field.value}
                          onChange={(e) => {
                            const newvalue = parseInt(e.target.value) || 0;
                            field.onChange(newvalue);
                          }}
                        />
                      )}
                      name={`workoutSet.${index}.rep`}
                      control={control}
                    />

                    <AddCircleIcon
                      type="button"
                      onClick={() => handleRemoveSet(index)}
                      variant="contained"
                      color="error"
                      sx={{ transform: "rotate(45deg)", cursor: "pointer" }}
                    />
                  </ListItem>
                );
              })}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <AddCircleIcon
                  type="button"
                  onClick={() => handleAddSet()}
                  variant="contained"
                  color="primary"
                />
              </Box>
            </List>
          </Box>

          {/* 保存ボタン */}
          <Button
            type="submit"
            variant="contained"
            color={
              currentCategory === "upperBody"
                ? "primary"
                : currentCategory === "lowerBody"
                  ? "error"
                  : "secondary"
            }
            fullWidth
          >
            {selectedTransaction ? "更新" : "保存"}
          </Button>
          {selectedTransaction && (
            <Button
              onClick={handleDelete}
              variant="outlined"
              color={"warning"}
              fullWidth
            >
              削除
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
export default TransactionForm;
